import { Redirect, useRouter, type UnknownOutputParams } from 'expo-router'
import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { ScrollView, View } from 'react-native'

import { ErrorBoundary } from '@/src/config/sentry'
import { PrayButton } from '@/src/elements/button/pray/PrayButton'
import { Message } from '@/src/elements/message'
import { PageTitle } from '@/src/elements/text/PageTitle'
import { Typography } from '@/src/elements/text/Typography'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { CommunityPrayer } from '@/src/features/community/ui/components/CommunityPrayer'
import { ShareSearchParamsAction } from '@/src/features/share/ui/components/ShareSearchParamsAction'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useSearchParamsAction } from '@/src/general/hooks/useSearchParamsAction'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { filterSearchParams } from '@/src/general/utils/filterSearchParams'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS, PUBLICATION_TRACKING_SOURCE, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { isFirefox } from '@/src/general/utils/isFirefox'
import { isWeb } from '@/src/general/utils/isWeb'
import { lazyWithRetries } from '@/src/general/utils/lazyWithRetries'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { usePublicationMarkAsReadMutation } from '../../../services/usePublicationMarkAsReadMutation'
import { usePublicationPrayMutation } from '../../../services/usePublicationPrayMutation'
import { usePublicationQuery } from '../../../services/usePublicationQuery'
import { removeDayPrefix } from '../../../utils/removeDayPrefix'
import { usePublicationRedirections } from '../../hooks/usePublicationRedirections'
import { useSharePublication } from '../../hooks/useSharePublication'
import { CommentSection } from './components/CommentSection'
import { PublicationContentPlaceholder } from './components/PublicationContentPlaceholder'
import { PublicationCoverImage } from './components/PublicationCoverImage'
import { PublicationDateLabel } from './components/PublicationDateLabel'
import { PublicationHeader } from './components/PublicationHeader'
import { PublicationLoadingScreen } from './components/PublicationLoadingScreen'
import { PublicationShareSection } from './components/PublicationShareSection'

const Content = lazyWithRetries(() =>
  import('./components/ProxyContent').then(({ ProxyContent }) => ({
    default: ProxyContent,
  })),
)

type TPublicationScreenContentProps = {
  publicationTrackingSource: PUBLICATION_TRACKING_SOURCE
  source: TRACKING_SOURCE
  id: number
  section?: 'comments'
  searchParams: UnknownOutputParams
}

export const PublicationScreen = ({
  id,
  section,
  source,
  publicationTrackingSource,
  searchParams,
}: TPublicationScreenContentProps): ReactNode => {
  const { userId } = filterSearchParams(searchParams)
  const isHereToComment = section === 'comments'
  const [isMarkedAsRead, setIsMarkedAsRead] = useState(false)
  const [isPageLoaded, setIsPageLoaded] = useState(false)
  const [pageY, setPageY] = useState(0)
  const layoutRef = useRef<ScrollView | null>(null)
  const commentSectionRef = useRef<View | null>(null)
  const { data: publication, isError, error } = usePublicationQuery(id)

  const router = useRouter()
  const { t } = useTranslate()
  const { routes } = useRoutes()
  const { share } = useSharePublication()
  const { mutateAsync, isPending: isPrayPending } = usePublicationPrayMutation({
    id,
    searchParams,
    communityLifetime: publication?.community.lifetime,
    communityId: publication?.community.id,
    hasJoinedCommunity: publication?.community.hasJoined,
  })
  const { mutate: markPublicationAsRead } = usePublicationMarkAsReadMutation({
    publicationId: id,
  })
  const me = useMeQuery()
  const isUsingFirefox = isWeb() && isFirefox()

  useSearchParamsAction({
    searchParams,
    action: ({ userId: actionUserId }) => {
      markPublicationAsRead({
        userId: Number(actionUserId),
        source: PUBLICATION_TRACKING_SOURCE.FROM_EMAIL,
      })
      setIsMarkedAsRead(true)
    },
    requestedQuery: {
      action: 'mark-as-read',
      userId: (value) => !!Number(value),
    },
  })

  useEffect(() => {
    if (me.data?.id && !userId && !isMarkedAsRead) {
      markPublicationAsRead({
        userId: me.data.id,
        source: publicationTrackingSource,
      })
      setIsMarkedAsRead(true)
    }
  }, [markPublicationAsRead, isMarkedAsRead, me.data?.id, publicationTrackingSource, userId])

  useEffect(() => {
    if (!isHereToComment || isPageLoaded || !publication) return
    const timer = window.setTimeout(() => {
      setIsPageLoaded(true)
      // oxlint-disable-next-line max-params
      commentSectionRef.current?.measure((_x, _y, _width, _height, _pageX, _pageY) => setPageY(_pageY))
    }, 500)
    return () => window.clearTimeout(timer)
  }, [isHereToComment, publication, isPageLoaded])

  useEffect(() => {
    if (!isPageLoaded) return
    if (isHereToComment) {
      layoutRef.current?.scrollTo({
        y: pageY,
        animated: true,
      })
    } else if (isUsingFirefox) {
      layoutRef.current?.scrollTo({
        y: 0,
        animated: false,
      })
    }
  }, [isHereToComment, isPageLoaded, publication, section, isUsingFirefox, pageY])

  const { redirectPath } = usePublicationRedirections(publication)
  if (redirectPath) return <Redirect href={redirectPath} />

  if (isError && !publication)
    return (
      <ErrorScreen error={error} defaultErrorKey="publication.load.error" testID={TEST_ID.PUBLICATION.SCREEN.ERROR} />
    )

  if (!publication) return <PublicationLoadingScreen />

  const { community } = publication

  return (
    <View className="flex-1">
      <PublicationHeader publicationId={id} />
      <ShareSearchParamsAction
        searchParams={searchParams}
        onShare={() => share(publication, TRACKING_SOURCE.EMAIL_LINK)}
      />
      <ScrollView ref={layoutRef} testID={TEST_ID.PUBLICATION.SCREEN.NAME} className="flex-1">
        <View className="copy-safe-content flex-column mx-auto flex w-full max-w-screen-md px-5">
          <View className="my-10">
            <PublicationDateLabel
              lifetime={community.lifetime}
              rank={publication.rank}
              effectivePublicationDate={publication.effectivePublicationDate}
            />
            <PageTitle>{removeDayPrefix(publication.name)}</PageTitle>
          </View>
          {publication.picture != null && <PublicationCoverImage source={getImageUrl(publication.picture)} />}
          <ErrorBoundary
            fallback={() => (
              <View className="h-32 items-center justify-center rounded-sm">
                <Typography colors="danger">{t('publication.load.error')}</Typography>
              </View>
            )}
          >
            <Suspense fallback={<PublicationContentPlaceholder />}>
              <Content content={publication.content} />
            </Suspense>
          </ErrorBoundary>
          <View className="py-4">
            <CommunityPrayer prayerContent={community.prayerContent} prayerName={community.prayerName} />
          </View>
          {!publication.hasPrayed && (
            <View className="pb-10 pt-2">
              <PrayButton
                isLoading={isPrayPending}
                className="mx-auto"
                label={t('pray.prayed')}
                onPress={() =>
                  mutateAsync().then(() => {
                    GTM.sendEvent(EVENTS.PRIERE_FINISHED, {
                      trackingSource: source,
                    })
                    router.navigate(routes.publicationByIdReward(publication.id, { source }))
                  })
                }
              />
            </View>
          )}
          {!!publication.hasPrayed && (
            <Message className="mb-16 mt-3" iconName={ICON.CHECK_CIRCLE_BROKEN} colors="success">
              {t('publication.prayed-by', {
                count: publication.prayersCount - 1,
              })}
            </Message>
          )}
          <PublicationShareSection publication={publication} />
          <CommentSection
            ref={commentSectionRef}
            show={isHereToComment}
            commentsCount={publication.commentsCount}
            publicationId={publication.id}
            communityId={community.id}
          />
        </View>
      </ScrollView>
    </View>
  )
}
